// ─── Zod validation-message → i18n key mapping (Batch B, B7, GHE #9/#47) ───
// docs/phase9/batch-b-i18n.md §2 "Zod validation messages" — Pattern A: schemas in
// `packages/shared/src/schemas` emit STABLE KEYS, not prose, as their custom
// `.min`/`.max`/`.regex`/`.refine` messages (e.g. `'color.invalidHex'`). The API keeps
// returning that raw key verbatim in the RFC 7807 `detail` — it is NEVER localized
// server-side. Only the client maps key -> localized text, via the dedicated
// `validation` namespace (`locales/{es,en}/validation.json`).
//
// A Zod issue's `.message` (and RHF's `FieldError['message']`) is a runtime `string`,
// so it cannot be narrowed to the typed `t()` key union at compile time — the same
// documented escape hatch as `features/access/RoleTooltip.tsx`'s `translateDynamicKey`
// (a single narrow cast, not a blanket `any`).
//
// Messages that predate this sweep (Zod's own built-in English defaults, e.g.
// "Required", or any not-yet-swept custom message) are not in the catalog. Using
// i18next's `defaultValue` makes those fall back to the raw message unchanged — still
// English, but visible — rather than leaking the raw key string to the user.
import type { TFunction } from 'i18next';

/** Bound to the `validation` namespace: `useTranslation('validation')`. */
export type ValidationTFunction = TFunction<'validation'>;

/**
 * Maps a Zod/RHF field-error message to its localized string when it is one of the
 * stable keys the B7 sweep introduced; otherwise returns the message unchanged.
 */
export function translateValidationMessage(
  t: ValidationTFunction,
  message: string | undefined,
): string | undefined {
  if (!message) return message;
  const translate = t as unknown as (key: string, options: { defaultValue: string }) => string;
  return translate(message, { defaultValue: message });
}

/**
 * #269 — the same mapping for a SERVER-side rejection, where the keys arrive inside an
 * RFC 7807 `detail` rather than on a form field.
 *
 * `handleError` (apps/api middleware) formats a ZodError as
 * `path.to.field: someKey; other.field: otherKey`, and every consumer used to render that
 * verbatim — so a user saw the literal string `scopeId: dashboard.scopeIdRequired`. This
 * translates the key half of each segment and drops the machine-readable path, which
 * names an API field the user never saw.
 *
 * Returns undefined when nothing in the string looked like a catalogued key, so callers
 * can fall back to their own generic copy rather than showing a half-translated blob.
 */
export function translateProblemDetail(
  t: ValidationTFunction,
  detail: string | undefined,
): string | undefined {
  if (!detail) return undefined;

  let translatedAny = false;
  const parts = detail.split('; ').map((segment) => {
    // Only the FIRST colon separates path from message — a message may contain one.
    const separator = segment.indexOf(': ');
    if (separator === -1) return segment;

    const message = segment.slice(separator + 2);
    const translated = translateValidationMessage(t, message);
    if (translated !== undefined && translated !== message) translatedAny = true;
    return translated ?? message;
  });

  return translatedAny ? parts.join(' ') : undefined;
}
